import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ClipboardCheck } from 'lucide-react';
import { getCurrentEvidencePanelCopy } from '@/components/proof-of-posting/submissionReferenceLabels';

interface Props {
  taskType?: 'POSTING' | 'REMOVAL' | 'REPOST' | string | null;
  attachedCount?: number;
  referenceCount?: number;
  variant?: 'card' | 'lightbox';
  children: ReactNode;
}

export function CurrentSubmissionEvidencePanel({
  taskType,
  attachedCount,
  referenceCount = 0,
  variant = 'card',
  children,
}: Props) {
  const copy = getCurrentEvidencePanelCopy(taskType, referenceCount);

  if (variant === 'lightbox') {
    return (
      <div className="flex min-w-0 flex-1 flex-col gap-3 rounded-xl border-2 border-sky-300 bg-sky-50/40 p-3 shadow-sm ring-1 ring-sky-200">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="gap-1 bg-sky-700 text-white hover:bg-sky-700">
                <ClipboardCheck className="h-3 w-3" />
                Under review
              </Badge>
              <span className="text-sm font-semibold text-foreground">{copy.title}</span>
              <span className="text-xs font-medium text-sky-800">{copy.subtitle}</span>
            </div>
            <p className="text-xs text-muted-foreground">{copy.description}</p>
          </div>
          {attachedCount != null ? (
            <Badge variant="outline" className="border-sky-300 bg-white text-sky-900">
              {attachedCount} attached
            </Badge>
          ) : null}
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-center">{children}</div>
      </div>
    );
  }

  return (
    <Card className="border-2 border-sky-300 bg-sky-50/20 ring-1 ring-sky-200">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-start justify-between gap-3 text-base">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="gap-1 bg-sky-700 text-white hover:bg-sky-700">
                <ClipboardCheck className="h-3 w-3" />
                Under review
              </Badge>
              <span>{copy.title}</span>
              <span className="text-xs font-medium text-sky-800">{copy.subtitle}</span>
            </div>
            <p className="text-xs font-normal text-muted-foreground">{copy.description}</p>
          </div>
          {attachedCount != null ? (
            <Badge variant="outline" className="border-sky-300 bg-white text-sky-900">
              {attachedCount} attached
            </Badge>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}
